// utils/ImageUtils.js

import imageCompression from "browser-image-compression";
import axiosInstance from "../api_calls/axiosInstance";
import Configs from "./Configs";
import AppConstants from "../constants/appConstants";

/**
 * Function to handle image selection, cropping, and uploading
 * @param {File} file - The file object selected by the user
 * @param {string} userId - The user's ID
 * @param {function} setErrorMessage - Function to set error messages
 * @param {function} setUploadStatus - Function to set upload status
 * @param {function} history - The history object to navigate
 * @param {number} maxSizeMB - Maximum file size in MB
 * @param {number} minWidth - Minimum image width in pixels
 * @param {number} minHeight - Minimum image height in pixels
 */
export const handleImageChange = async (
  file,
  userId,
  setErrorMessage,
  setUploadStatus,
  history,
  maxSizeMB = AppConstants.PHOTO_UPLOAD_MAX_SIZE_MB,
  minWidth = AppConstants.PHOTO_MIN_WIDTH_PX,
  minHeight = AppConstants.PHOTO_MIN_HEIGHT_PX
) => {
  const fileSizeMB = file.size / (1024 * 1024); // Convert bytes to MB

  if (fileSizeMB > maxSizeMB) {
    setErrorMessage(
      `The selected photo is too large. Please choose a photo smaller than ${maxSizeMB} MB.`
    );
    return false;
  }

  try {
    setUploadStatus("Cropping the image...");

    // Compress and crop the image
    const compressedFile = await compressImage(file);

    // Validate size after compression
    const compressedSizeMB = compressedFile.size / (1024 * 1024);
    if (compressedSizeMB > maxSizeMB) {
      setErrorMessage("The image is still too large after compression.");
      return false;
    }

    const image = new Image();
    image.src = URL.createObjectURL(compressedFile);

    // Wrapped in a Promise so the actual upload result (not just "the image
    // finished decoding") propagates back to the caller — previously this
    // function resolved before onload ever fired, so callers had no way to
    // know whether uploadImage actually succeeded.
    return await new Promise((resolve) => {
      image.onload = async () => {
        if (image.width < minWidth || image.height < minHeight) {
          setErrorMessage(
            `The image is too small. Minimum size is ${minWidth}x${minHeight} pixels.`
          );
          resolve(false);
          return;
        }

        setUploadStatus("Uploading image...");
        const uploaded = await uploadImage(
          compressedFile,
          userId,
          setUploadStatus,
          setErrorMessage,
          history
        );
        resolve(uploaded);
      };
      image.onerror = () => {
        setErrorMessage("Error while selecting the image.");
        resolve(false);
      };
    });
  } catch (error) {
    setErrorMessage("Error while selecting the image.");
    return false;
  }
};

/**
 * Compress the selected image
 * @param {File} file - The file to be compressed
 */
export const compressImage = async (file) => {
  const options = {
    maxWidthOrHeight: AppConstants.PHOTO_COMPRESSION_MAX_DIMENSION_PX,
    useWebWorker: true,
    maxSizeMB: AppConstants.PHOTO_UPLOAD_MAX_SIZE_MB,
    fileType: "image/webp", //file.type
  };

  return await imageCompression(file, options);
};

/**
 * Upload an additional photo to the server
 * @param {File} file - The image file to upload
 * @param {string} userId - The user's ID
 * @param {function} setUploadStatus - Function to set upload status
 * @param {function} setErrorMessage - Function to set error messages
 * @param {function} history - The history object to navigate
 */
export const uploadAdditionalPhoto = async (
  file,
  userId,
  setUploadStatus,
  setErrorMessage,
  history
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user-id", userId);

    const response = await axiosInstance.post(
      Configs.uploadPhotoEp,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      }
    );

    if (response.data?.status === "success") {
      setUploadStatus("Additional photo uploaded successfully!");
      // Optionally navigate somewhere after success:
      // history.push("/some-path");
      return true;
    } else {
      setErrorMessage(
        response.data?.data?.message === "limit_reached"
          ? "You've reached the maximum number of photos allowed."
          : "Additional photo upload failed"
      );
      return false;
    }
  } catch (error) {
    setErrorMessage("Error uploading additional photo");
    return false;
  }
};

/**
 * Upload the image to the server
 * @param {File} file - The image file to upload
 * @param {string} userId - The user's ID
 * @param {function} setUploadStatus - Function to set upload status
 * @param {function} setErrorMessage - Function to set error messages
 * @param {function} history - The history object to navigate
 */
export const uploadImage = async (
  file,
  userId,
  setUploadStatus,
  setErrorMessage,
  history
) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user-id", userId);

    // Upload image using axios
    const response = await axiosInstance.post(
      Configs.uploadProfilePhotoEp,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      }
    );

    if (response.data?.status === "success") {
      setUploadStatus("Upload successful!");
      //   history.push("/thumbnails-page"); // Navigate to another page if upload is successful
      return true;
    } else {
      setErrorMessage("Photo upload failed");
      return false;
    }
  } catch (error) {
    setErrorMessage("Error uploading image");
    return false;
  }
};

/**
 * Delete the image
 * @param {number} photoId - The photo ID to delete
 * @param {function} setErrorMessage - Function to set error messages
 */
export const deleteImage = async (photoId, setErrorMessage) => {
  try {
    // Previously hardcoded as "/myprofile/delete-photo/", a route that
    // doesn't exist on the backend (confirmed against pmm/myprofile/urls.py
    // — only "api-delete-photo/" is registered) and would 404. Routing
    // through Configs.deletePhotoEp both centralizes it and fixes that.
    const response = await axiosInstance.post(Configs.deletePhotoEp, {
      photo_id: photoId,
    });

    if (response.data?.status === "success") {
      setErrorMessage("Photo deleted successfully");
      return true;
    } else {
      setErrorMessage("Failed to delete photo");
      return false;
    }
  } catch (error) {
    setErrorMessage("Failed to delete photo");
    return false;
  }
};

/**
 * Utility to get the cropped image
 * @param {string} imageSrc - The image source (base64 encoded)
 * @param {Object} croppedAreaPixels - The pixels of the cropped area, in source-image coordinates
 * @param {number} rotation - The rotation angle of the crop (optional)
 */
export const getCroppedImg = (imageSrc, croppedAreaPixels, rotation = 0) => {
  const image = new Image();
  image.src = imageSrc;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  return new Promise((resolve, reject) => {
    image.onload = () => {
      const { x, y, width, height } = croppedAreaPixels;

      // Set canvas size based on the cropped area
      canvas.width = width;
      canvas.height = height;

      // Apply the crop to the canvas with rotation (if any)
      ctx.translate(width / 2, height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(
        image,
        x,
        y,
        width,
        height,
        -width / 2,
        -height / 2,
        width,
        height
      );

      // Convert the canvas content to a base64 URL
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Failed to create the cropped image."));
            return;
          }
          const file = new File([blob], "cropped-image.jpg", {
            type: "image/jpeg",
          });
          resolve(file); // Return the cropped file
        },
        "image/jpeg",
        1 // Quality (optional)
      );
    };
    image.onerror = (error) => reject(error);
  });
};
